/**
 * Visitor-funnel relay (plan Part H1).
 *
 * embed.js runs on third-party customer sites, so this module deliberately:
 *   - does NOT load posthog-js and never touches cookies / localStorage /
 *     sessionStorage (the per-load id lives in memory only);
 *   - sends a handful of allow-listed, content-free events to OUR server
 *     (`POST {baseUrl}/api/widget/analytics`), which validates them and
 *     forwards to PostHog;
 *   - is fire-and-forget (sendBeacon, falling back to fetch keepalive) and
 *     NEVER throws;
 *   - honours opt-out: `data-analytics="off"`, `"analytics": false` in
 *     `data-config`, and the browser's Do Not Track / Global Privacy Control.
 *
 * Identity: `cid` (random per page load, in memory) is sent with EVERY event and is the only thing the
 * server uses as PostHog distinct_id, so the whole funnel of one page load (loaded -> opened -> requested ->
 * connected -> ...) is one person. `sid` (the conversation / room name) is added once a session exists and
 * becomes the `conversation_id` property server-side: the join key to the server lifecycle events.
 *
 * The server-side allow-list lives in src/app/api/widget/analytics/allow-list.ts.
 */
import type { EventBus } from './event-bus';

export type RelayEventName =
  | 'widget_loaded'
  | 'widget_opened'
  | 'conversation_requested'
  | 'mic_permission_result'
  | 'conversation_connected'
  | 'agent_ready'
  | 'conversation_ended_client'
  | 'widget_error'
  | 'widget_prewarm_fallback';

export type EndedBy = 'user_end' | 'user_close' | 'drop';

export interface AnalyticsRelay {
  readonly enabled: boolean;
  track(name: RelayEventName, props?: Record<string, string | number | boolean>): void;
  /** Conversation id (room name) once a session exists; null clears it. Sent as `sid`; `cid` never changes. */
  setSession(id: string | null): void;
  /** Mark how the NEXT sessionEnd should be attributed (consumed once). */
  noteEndedBy(by: EndedBy): void;
  consumeEndedBy(): EndedBy | null;
  /** Timestamp of the last `conversation_requested` (consumed once). */
  consumeRequestedAt(): number | null;
}

type NavLike = {
  doNotTrack?: string | null;
  globalPrivacyControl?: boolean;
  language?: string;
  sendBeacon?: (url: string, data?: BodyInit | null) => boolean;
};

const ERROR_CODES = new Set(['CONNECT_FAILED', 'MIC_ENABLE_FAILED', 'MIC_TOGGLE_FAILED', 'SEND_TEXT_FAILED']);

/** True when the site owner or the visitor has opted out of analytics. */
export function isAnalyticsOptedOut(
  attr: string | null | undefined,
  rawConfig: string | null | undefined,
  nav: NavLike | undefined = typeof navigator !== 'undefined' ? (navigator as NavLike) : undefined,
): boolean {
  const a = (attr ?? '').trim().toLowerCase();
  if (a === 'off' || a === 'false' || a === '0' || a === 'no') return true;
  if (rawConfig) {
    try {
      const cfg = JSON.parse(rawConfig);
      if (cfg && typeof cfg === 'object' && (cfg as { analytics?: unknown }).analytics === false) return true;
    } catch {
      /* parseConfig already warns about bad JSON */
    }
  }
  if (nav) {
    const dnt = nav.doNotTrack;
    if (dnt === '1' || dnt === 'yes') return true;
    if (nav.globalPrivacyControl === true) return true;
  }
  return false;
}

/** Random per-page-load id. In memory only - never persisted. */
export function randomLoadId(): string {
  try {
    const c = (typeof crypto !== 'undefined' ? crypto : undefined) as Crypto | undefined;
    if (c?.getRandomValues) {
      const b = new Uint8Array(12);
      c.getRandomValues(b);
      return 'e' + Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    /* fall through */
  }
  return 'e' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
}

export interface RelayOptions {
  baseUrl: string;
  apiKey: string;
  optOut?: boolean;
  source?: 'embed' | 'hosted_site';
  nav?: NavLike;
  fetchImpl?: typeof fetch;
}

const DISABLED: AnalyticsRelay = {
  enabled: false,
  track() {},
  setSession() {},
  noteEndedBy() {},
  consumeEndedBy: () => null,
  consumeRequestedAt: () => null,
};

export function createAnalyticsRelay(opts: RelayOptions): AnalyticsRelay {
  try {
    if (opts.optOut || !opts.apiKey || !opts.baseUrl) return DISABLED;
    const nav: NavLike | undefined = opts.nav ?? (typeof navigator !== 'undefined' ? (navigator as NavLike) : undefined);
    const url = `${opts.baseUrl.replace(/\/+$/, '')}/api/widget/analytics`;
    const cid = randomLoadId();
    const source = opts.source ?? 'embed';
    const locale = typeof nav?.language === 'string' ? nav.language : undefined;
    let sid: string | null = null;
    let endedBy: EndedBy | null = null;
    let requestedAt: number | null = null;

    const send = (json: string) => {
      try {
        // text/plain keeps this a CORS "simple request": no preflight, and it is
        // what sendBeacon can send cross-origin without surprises.
        if (nav?.sendBeacon && nav.sendBeacon(url, new Blob([json], { type: 'text/plain' }))) return;
        const f = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
        if (!f) return;
        void f(url, {
          method: 'POST',
          body: json,
          headers: { 'Content-Type': 'text/plain' },
          keepalive: true,
          credentials: 'omit',
        }).catch(() => undefined);
      } catch {
        /* never throw */
      }
    };

    return {
      enabled: true,
      track(name, props) {
        try {
          if (name === 'conversation_requested') requestedAt = Date.now();
          send(JSON.stringify({ apiKey: opts.apiKey, source, cid, sid, locale, events: [{ name, props: props ?? {} }] }));
        } catch {
          /* never throw */
        }
      },
      setSession(id) {
        sid = id;
      },
      noteEndedBy(by) {
        endedBy = by;
      },
      consumeEndedBy() {
        const v = endedBy;
        endedBy = null;
        return v;
      },
      consumeRequestedAt() {
        const v = requestedAt;
        requestedAt = null;
        return v;
      },
    };
  } catch {
    return DISABLED;
  }
}

/**
 * Subscribe to the embed eventBus and relay the funnel events that are derived
 * from it. Never reads transcript/tool/citation/scene payloads.
 */
export function attachAnalyticsBridge(relay: AnalyticsRelay, eventBus: EventBus, now: () => number = Date.now): void {
  if (!relay.enabled) return;
  let connectedAt: number | null = null;

  eventBus.on('sessionStart', (p: { roomName?: unknown } | undefined) => {
    try {
      relay.setSession(typeof p?.roomName === 'string' ? p.roomName : null);
      connectedAt = now();
      const requestedAt = relay.consumeRequestedAt();
      relay.track('conversation_connected', requestedAt != null ? { time_to_connect_ms: Math.max(0, connectedAt - requestedAt) } : {});
    } catch {
      /* never throw into the host's event handlers */
    }
  });
  eventBus.on('agentReady', () => {
    try {
      relay.track('agent_ready', connectedAt != null ? { time_to_agent_ms: Math.max(0, now() - connectedAt) } : {});
    } catch {
      /* noop */
    }
  });
  eventBus.on('sessionEnd', () => {
    try {
      const ended_by = relay.consumeEndedBy() ?? 'drop';
      relay.track(
        'conversation_ended_client',
        connectedAt != null ? { ended_by, duration_s: Math.round((now() - connectedAt) / 1000) } : { ended_by },
      );
      connectedAt = null;
      relay.setSession(null);
    } catch {
      /* noop */
    }
  });
  eventBus.on('error', (e: { code?: unknown } | undefined) => {
    try {
      const code = typeof e?.code === 'string' && ERROR_CODES.has(e.code) ? e.code : 'UNKNOWN';
      relay.track('widget_error', { code });
      if (code === 'MIC_ENABLE_FAILED') relay.track('mic_permission_result', { granted: false });
    } catch {
      /* noop */
    }
  });
}
